import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { App as CapacitorApp } from "@capacitor/app";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { fetchUser } from "@/modules/auth/store/authSlice";
import { fetchNotifications } from "@/modules/notifications/store/notificationsSlice";
import { checkSubscription } from "@/modules/subscriptions/store/subscriptionSlice";
import {
  isNativeApp,
  getNativePlatform,
  isStandaloneMode,
} from "@/utils/mobileShell";

const RESUME_SYNC_INTERVAL_MS = 15000;

// Routes where the Android back button should leave the app instead of going back in history.
const ROOT_PATHS = new Set(["/", "/tenders", "/dashboard", "/login"]);

function bindCapacitorListener(plugin, eventName, handler, assignRemove) {
  if (!plugin?.addListener) return;

  try {
    const result = plugin.addListener(eventName, handler);

    if (result && typeof result.then === "function") {
      result
        .then((listener) => {
          assignRemove(() => listener?.remove?.());
        })
        .catch(() => {});
      return;
    }

    assignRemove(() => result?.remove?.());
  } catch (_) {
    // Ignore plugin listener binding failures in web/native shells.
  }
}

/**
 * Turns a deep link into an in-app path.
 * Handles both https://app.tarhplatform.com/payment/result?... (App Links / Universal Links)
 * and com.tarhplatform.app://payment/result?... (custom scheme).
 */
export function deepLinkToPath(url) {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const isCustomScheme = !/^https?:$/.test(parsed.protocol);
    const pathname = isCustomScheme
      ? `/${parsed.host || ""}${parsed.pathname || ""}`.replace(/\/+$/, "") || "/"
      : parsed.pathname || "/";
    return `${pathname}${parsed.search || ""}${parsed.hash || ""}`;
  } catch (_) {
    return null;
  }
}

export default function MobileRuntimeBridge() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const lastSyncAtRef = useRef(0);
  const locationRef = useRef(location);
  locationRef.current = location;

  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("native-shell", isNativeApp());
    root.classList.toggle("standalone-shell", isStandaloneMode());
    root.classList.toggle("native-android-shell", getNativePlatform() === "android");
    root.classList.toggle("native-ios-shell", getNativePlatform() === "ios");

    if (!isNativeApp()) return undefined;

    const applyStatusBar = () => {
      const dark = root.classList.contains("dark");
      StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light }).catch(() => {});
      if (getNativePlatform() === "android") {
        StatusBar.setBackgroundColor({ color: dark ? "#0f172a" : "#ffffff" }).catch(() => {});
      }
    };
    applyStatusBar();
    const observer = new MutationObserver(applyStatusBar);
    observer.observe(root, { attributes: true, attributeFilter: ["class"] });

    // The web app has rendered; drop the native splash.
    const splashTimer = window.setTimeout(() => {
      SplashScreen.hide({ fadeOutDuration: 200 }).catch(() => {});
    }, 150);

    return () => {
      observer.disconnect();
      window.clearTimeout(splashTimer);
    };
  }, []);

  useEffect(() => {
    const syncOnResume = async () => {
      const now = Date.now();
      if (now - lastSyncAtRef.current < RESUME_SYNC_INTERVAL_MS) return;
      lastSyncAtRef.current = now;

      await Promise.allSettled([
        dispatch(fetchUser()),
        dispatch(checkSubscription()),
        dispatch(fetchNotifications()),
      ]);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        syncOnResume();
      }
    };

    const onWindowFocus = () => {
      syncOnResume();
    };

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onWindowFocus);

    let removeAppState = null;
    let removeAppUrlOpen = null;
    let removeBackButton = null;

    if (isNativeApp()) {
      bindCapacitorListener(
        CapacitorApp,
        "appStateChange",
        ({ isActive }) => {
          if (isActive) {
            syncOnResume();
          }
        },
        (remove) => {
          removeAppState = remove;
        }
      );

      bindCapacitorListener(
        CapacitorApp,
        "appUrlOpen",
        ({ url }) => {
          const nextPath = deepLinkToPath(url);
          const current = locationRef.current;
          if (nextPath && nextPath !== `${current.pathname}${current.search}${current.hash}`) {
            navigate(nextPath);
          }
        },
        (remove) => {
          removeAppUrlOpen = remove;
        }
      );

      bindCapacitorListener(
        CapacitorApp,
        "backButton",
        async ({ canGoBack }) => {
          const path = locationRef.current.pathname;
          if (canGoBack && !ROOT_PATHS.has(path)) {
            navigate(-1);
            return;
          }
          await CapacitorApp.exitApp?.();
        },
        (remove) => {
          removeBackButton = remove;
        }
      );

      // Cold start from a deep link: the appUrlOpen event may have fired before React mounted.
      CapacitorApp.getLaunchUrl?.()
        .then((launch) => {
          const nextPath = deepLinkToPath(launch?.url);
          if (nextPath && nextPath !== "/" && nextPath !== locationRef.current.pathname) {
            navigate(nextPath, { replace: true });
          }
        })
        .catch(() => {});
    }

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onWindowFocus);
      removeAppState?.();
      removeAppUrlOpen?.();
      removeBackButton?.();
    };
  }, [dispatch, navigate]);

  return null;
}
